import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaRegEdit } from 'react-icons/fa';
import { BsCalendar4 } from 'react-icons/bs';
import { TbClockPlus } from 'react-icons/tb';
import { FaIndianRupeeSign } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import { useUser } from '../../context/UserContext';
import Stack from '@mui/material/Stack';
import Pagination from '@mui/material/Pagination';
import { format } from 'date-fns';
import api from '../../utils/api';

// ─── Constants ────────────────────────────────────────────────────────────────

const TIME_CONFIG = {
  FULL_DAY:    { label: 'Full Day',    duration: 1   },
  FIRST_HALF:  { label: 'First Half',  duration: 0.5 },
  SECOND_HALF: { label: 'Second Half', duration: 0.5 },
};

const TYPE_LABELS = {
  COMP_OFF: 'Comp Off',
  COMP_PAY: 'Comp Pay',
};

// ─── Status config ────────────────────────────────────────────────────────────

const statusConfig = {
  APPROVED:    { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', border: 'border-emerald-200', label: 'Approved'    },
  PENDING:     { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-400',   border: 'border-amber-200',   label: 'Pending'     },
  REJECTED:    { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500',     border: 'border-red-200',     label: 'Rejected'    },
  CANCELLED:   { bg: 'bg-slate-100',  text: 'text-slate-500',   dot: 'bg-slate-400',   border: 'border-slate-200',   label: 'Cancelled'   },
  NOT_APPLIED: { bg: 'bg-slate-50',   text: 'text-slate-500',   dot: 'bg-slate-300',   border: 'border-slate-200',   label: 'Not Applied' },
};

const sessionLabel = (t) =>
  t === 'FULL_DAY' ? 'Full Day' : t === 'FIRST_HALF' ? 'First Half' : t === 'SECOND_HALF' ? 'Second Half' : '—';

const sessionColor = {
  FULL_DAY:    'bg-blue-100 text-blue-700',
  FIRST_HALF:  'bg-violet-100 text-violet-700',
  SECOND_HALF: 'bg-indigo-100 text-indigo-700',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const safeFormat = (dateStr, fmt) => {
  try { return format(new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00'), fmt); }
  catch { return dateStr; }
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusPill = ({ status }) => {
  const cfg = statusConfig[status] || statusConfig.NOT_APPLIED;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  if (!type) return <span className="text-slate-300 text-sm">—</span>;
  const isOff = type === 'COMP_OFF';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
      isOff
        ? 'bg-blue-50 text-blue-700 border border-blue-200'
        : 'bg-violet-50 text-violet-700 border border-violet-200'
    }`}>
      {isOff ? <TbClockPlus className="text-sm" /> : <FaIndianRupeeSign className="text-[11px]" />}
      {TYPE_LABELS[type]}
    </span>
  );
};

// Session selector — button group, avoids re-mount flicker
const SessionSelector = ({ value, onChange }) => (
  <div className="grid grid-cols-3 gap-2">
    {Object.entries(TIME_CONFIG).map(([key, cfg]) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        className={`py-2.5 px-1 rounded-xl border text-xs font-semibold transition-colors ${
          value === key
            ? 'bg-slate-800 border-slate-800 text-white shadow-sm'
            : 'border-slate-200 text-slate-600 hover:bg-slate-50 bg-white'
        }`}
      >
        {cfg.label}
      </button>
    ))}
  </div>
);

// Type selector inside the edit/apply form
const TypeSelector = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2">
    <button
      type="button"
      onClick={() => onChange('COMP_OFF')}
      className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border text-xs font-semibold transition-colors ${
        value === 'COMP_OFF'
          ? 'bg-blue-700 border-blue-700 text-white shadow-sm'
          : 'border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100'
      }`}
    >
      <TbClockPlus className="text-base" />
      Comp Off
      {value === 'COMP_OFF' && (
        <span className="text-[10px] font-normal opacity-80">Auto approved</span>
      )}
    </button>
    <button
      type="button"
      onClick={() => onChange('COMP_PAY')}
      className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border text-xs font-semibold transition-colors ${
        value === 'COMP_PAY'
          ? 'bg-violet-700 border-violet-700 text-white shadow-sm'
          : 'border-violet-200 text-violet-700 bg-violet-50 hover:bg-violet-100'
      }`}
    >
      <FaIndianRupeeSign className="text-base" />
      Comp Pay
      {value === 'COMP_PAY' && (
        <span className="text-[10px] font-normal opacity-80">Manager approval</span>
      )}
    </button>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

function E_Comps() {
  const { currentUser, loading } = useUser();

  const [rows,        setRows]        = useState([]);
  const [isLoading,   setIsLoading]   = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 6;

  // Single unified form modal
  // formMode: 'apply' | 'edit'
  // formType: 'COMP_OFF' | 'COMP_PAY' (selected inside form)
  const [showForm,     setShowForm]     = useState(false);
  const [formMode,     setFormMode]     = useState(null);
  const [formRow,      setFormRow]      = useState(null);
  const [formTiming,   setFormTiming]   = useState('FULL_DAY');
  const [formType,     setFormType]     = useState('COMP_OFF');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // View modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRow,       setViewRow]       = useState(null);

  const totalPages    = Math.ceil(rows.length / rowsPerPage);
  const paginatedRows = rows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  // ── Data fetch ─────────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    if (!currentUser?.id) { setRows([]); setIsLoading(false); return; }
    setIsLoading(true);
    try {
      const now = new Date();

      const [attendRes, coRes, cpRes] = await Promise.all([
        api.get('/api/daily-attendance/compoff-eligible', {
          params: { userId: currentUser.id, month: now.getMonth() + 1, year: now.getFullYear() },
        }),
        api.get(`/api/compoffs/${currentUser.id}`),
        api.get(`/api/comppay/${currentUser.id}`),
      ]);

      const eligibleAttendance = Array.isArray(attendRes.data) ? attendRes.data : [];
      const compOffs = Array.isArray(coRes.data) ? coRes.data : [];
      const compPays = Array.isArray(cpRes.data)
        ? cpRes.data
        : Array.isArray(cpRes.data?.content) ? cpRes.data.content : [];

      const coByDate = {};
      compOffs.forEach(co => { coByDate[toYMD(new Date(co.onDate))] = co; });

      const cpByDate = {};
      compPays.forEach(cp => { if (cp.onDate) cpByDate[toYMD(new Date(cp.onDate))] = cp; });

      const dateSet = new Set([
        ...eligibleAttendance.map(a => toYMD(new Date(a.date))),
        ...Object.keys(coByDate),
        ...Object.keys(cpByDate),
      ]);

      const merged = Array.from(dateSet)
        .map(dateStr => {
          const co = coByDate[dateStr];
          const cp = cpByDate[dateStr];

          // CompOff record takes priority
          if (co) return {
            id:              co.id,
            date:            dateStr,
            timing:          co.timing       || 'FULL_DAY',
            status:          co.comOffStatus || 'APPROVED',
            type:            'COMP_OFF',
            expiresOn:       co.expiresOn    || null,
            approverComment: co.approverComment || '',
          };

          if (cp) return {
            id:              cp.id,
            date:            dateStr,
            timing:          cp.timing || 'FULL_DAY',
            status:          cp.compayStatus || cp.status || 'PENDING',
            type:            'COMP_PAY',
            expiresOn:       null,
            approverComment: cp.approverComment || '',
          };

          // Present on holiday/weekend — not yet applied
          return {
            id:              null,
            date:            dateStr,
            timing:          null,
            status:          'NOT_APPLIED',
            type:            null,
            expiresOn:       null,
            approverComment: '',
          };
        })
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      setRows(merged);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load compensatory records');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.id]);

  useEffect(() => { if (!loading) fetchAll(); }, [loading, fetchAll]);

  // ── Edit eligibility ───────────────────────────────────────────────────────
  //
  // CompOff APPROVED (not expired)  → can edit (change session OR switch to CompPay)
  // CompPay PENDING                 → can edit (change session OR switch to CompOff)
  // CompPay REJECTED                → can edit (re-apply as CompOff or re-submit CompPay)
  // NOT_APPLIED                     → show Apply button

  const canEdit = (row) => {
    if (!row.id) return false;
    if (row.type === 'COMP_OFF') {
      return row.status === 'APPROVED' && row.expiresOn && new Date(row.expiresOn) > new Date();
    }
    // CompPay: editable when PENDING or REJECTED
    return row.status === 'PENDING' || row.status === 'REJECTED';
  };

  const isExpired = (row) =>
    row.type === 'COMP_OFF' &&
    row.status === 'APPROVED' &&
    row.expiresOn &&
    new Date(row.expiresOn) <= new Date();

  // ── Open form handlers ─────────────────────────────────────────────────────

  const openApplyForm = (row) => {
    setFormRow(row);
    setFormTiming('FULL_DAY');
    setFormType('COMP_OFF');   // default to CompOff for fresh apply
    setFormMode('apply');
    setShowForm(true);
  };

  const openEditForm = (row) => {
    setFormRow(row);
    setFormTiming(row.timing || 'FULL_DAY');
    setFormType(row.type);    // pre-select current type; user can switch
    setFormMode('edit');
    setShowForm(true);
  };

  const closeForm = () => {
    if (isSubmitting) return;
    setShowForm(false);
    setFormRow(null);
    setFormMode(null);
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  //
  // Switching type on edit:
  //   CompOff → CompPay : DELETE old CompOff, POST new CompPay (PENDING)
  //   CompPay → CompOff : DELETE old CompPay, POST new CompOff (APPROVED)
  //   Same type         : PUT to update session/duration

  const handleFormSubmit = async () => {
    if (!formRow || !formMode || isSubmitting) return;
    setIsSubmitting(true);

    const payload = {
      onDate:     formRow.date,
      duration:   TIME_CONFIG[formTiming]?.duration || 1,
      timing:     formTiming,
      employeeId: currentUser.id,
    };

    try {
      if (formMode === 'apply') {
        // Fresh apply — no existing record
        if (formType === 'COMP_OFF') {
          await api.post(`/api/compoffs/apply?employeeId=${currentUser.id}`, payload);
          toast.success('Comp Off applied — auto approved ✓');
        } else {
          await api.post(`/api/comppay/apply?employeeId=${currentUser.id}`, payload);
          toast.success('Comp Pay submitted — awaiting manager approval');
        }

      } else if (formMode === 'edit') {
        const typeChanged = formRow.type !== formType;

        if (!typeChanged) {
          // Same type — just update session
          if (formType === 'COMP_OFF') {
            await api.put(`/api/compoffs/${formRow.id}?employeeId=${currentUser.id}`, payload);
            toast.success('Comp Off updated ✓');
          } else {
            await api.put(`/api/comppay/${formRow.id}?employeeId=${currentUser.id}`, payload);
            toast.success('Comp Pay updated ✓');
          }
        } else {
          // Type switched — delete old, create new
          if (formRow.type === 'COMP_OFF') {
            // Was CompOff → switching to CompPay
            await api.delete(`/api/compoffs/${formRow.id}`, { params: { employeeId: currentUser.id } });
            await api.post(`/api/comppay/apply?employeeId=${currentUser.id}`, payload);
            toast.success('Switched to Comp Pay — awaiting manager approval');
          } else {
            // Was CompPay → switching to CompOff
            await api.delete(`/api/comppay/${formRow.id}`, { params: { employeeId: currentUser.id } });
            await api.post(`/api/compoffs/apply?employeeId=${currentUser.id}`, payload);
            toast.success('Switched to Comp Off — auto approved ✓');
          }
        }
      }

      // ✅ LOGOUT FIX: Only re-fetch data — never reload the page
      setShowForm(false);
      setFormRow(null);
      setCurrentPage(1);
      await fetchAll();

    } catch (err) {
      // ✅ LOGOUT FIX: Catch error locally — don't let it bubble to the axios
      // interceptor which may redirect to /login on any non-2xx response
      console.error('Comp submit error:', err);
      const msg = err?.response?.data?.message || err?.response?.data || 'Failed to submit. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Failed to submit. Please try again.');
      // Do NOT rethrow — prevents interceptor redirect
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Form title ─────────────────────────────────────────────────────────────

  const formTitle = formMode === 'apply'
    ? 'Apply for Compensation'
    : formRow
      ? `Edit ${TYPE_LABELS[formRow.type] || 'Compensation'}`
      : '';

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="sm:p-6 sm:mt-auto mt-8 mb-20 sm:mb-auto w-full mx-auto">

      {/* Header */}
      <motion.div
        className="flex items-center mb-6 bg-blue-100 rounded-xl p-4 sm:p-5 shadow-sm border border-slate-200 gap-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      >
        <div className="p-2 rounded-xl bg-slate-700 text-slate-100 shrink-0">
          <TbClockPlus className="text-2xl" />
        </div>
        <h2 className="text-xl xs:text-2xl font-bold text-slate-800">My Holiday Duties</h2>
      </motion.div>

      {/* ── Desktop Table ─────────────────────────────────────────────────────── */}
      <motion.div
        className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white border-b border-slate-100">
              <tr>
                {['SL No', 'Date', 'Session', 'Status', 'Type', 'Expires On', 'Actions'].map(h => (
                  <th key={h} className="px-6 py-4 text-left text-sm font-semibold text-slate-600 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {isLoading ? (
                [1, 2, 3].map(i => (
                  <tr key={i}>
                    {[...Array(7)].map((_, j) => (
                      <td key={j} className="px-6 py-4">
                        <div className="h-4 bg-slate-100 rounded animate-pulse" style={{ width: `${50 + j * 7}%` }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : paginatedRows.length > 0 ? (
                paginatedRows.map((row, idx) => (
                  <motion.tr
                    key={`${row.date}-${row.id}`}
                    className="hover:bg-slate-50/60 transition-colors duration-150"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.03 }}
                  >
                    <td className="px-6 py-4 text-sm text-slate-500">
                      {(currentPage - 1) * rowsPerPage + idx + 1}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-800">
                      {safeFormat(row.date, 'dd-MM-yyyy')}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {row.timing ? sessionLabel(row.timing) : '—'}
                    </td>
                    <td className="px-6 py-4"><StatusPill status={row.status} /></td>
                    <td className="px-6 py-4"><TypeBadge type={row.type} /></td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {row.type === 'COMP_OFF' && row.status === 'APPROVED' && row.expiresOn
                        ? safeFormat(row.expiresOn, 'dd-MM-yyyy') : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">

                        {/* Apply — only when not yet applied */}
                        {row.status === 'NOT_APPLIED' && (
                          <motion.button
                            onClick={() => openApplyForm(row)}
                            className="px-3 py-1.5 text-xs font-semibold bg-slate-800 text-white rounded-full hover:bg-slate-700 transition-colors"
                            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                          >Apply</motion.button>
                        )}

                        {/* Edit:
                            CompOff APPROVED (not expired) → edit (can switch to CompPay)
                            CompPay PENDING                → edit (can switch to CompOff)
                            CompPay REJECTED               → edit (re-apply as either type) */}
                        {canEdit(row) && (
                          <motion.button
                            onClick={() => openEditForm(row)}
                            className="w-8 h-8 flex items-center justify-center rounded-xl bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors"
                            whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} title="Edit"
                          ><FaRegEdit className="text-base" /></motion.button>
                        )}

                        {/* View — for CANCELLED, expired CompOff */}
                        {(row.status === 'CANCELLED' || isExpired(row)) && (
                          <motion.button
                            onClick={() => { setViewRow(row); setShowViewModal(true); }}
                            className="w-8 h-8 flex items-center justify-center rounded-xl bg-blue-50 text-blue-500 hover:bg-blue-100 transition-colors"
                            whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} title="View"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </motion.button>
                        )}

                        {/* CompPay APPROVED — view only, nothing to edit */}
                        {row.type === 'COMP_PAY' && row.status === 'APPROVED' && (
                          <motion.button
                            onClick={() => { setViewRow(row); setShowViewModal(true); }}
                            className="w-8 h-8 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                            whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} title="View"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </motion.button>
                        )}

                      </div>
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-14 text-center text-slate-400 text-sm">
                    No holiday duty records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-slate-100">
          <Stack spacing={2} className="flex justify-end">
            <Pagination
              count={totalPages} page={currentPage}
              onChange={(_, p) => setCurrentPage(p)}
              variant="outlined" shape="rounded-full" size="small"
              showFirstButton showLastButton
              sx={{
                '& .MuiPaginationItem-root': { color: '#475569', borderColor: '#e2e8f0' },
                '& .Mui-selected': { backgroundColor: '#475569 !important', color: '#fff' },
              }}
            />
          </Stack>
        </div>
      </motion.div>

      {/* ── Mobile Cards ──────────────────────────────────────────────────────── */}
      <div className="sm:hidden space-y-3">
        {isLoading ? (
          [1, 2, 3].map(i => (
            <div key={i} className="bg-white p-4 rounded-2xl border border-slate-100 animate-pulse">
              <div className="flex justify-between mb-3">
                <div className="h-4 w-28 bg-slate-200 rounded-full" />
                <div className="h-6 w-20 bg-slate-200 rounded-full" />
              </div>
              <div className="h-px bg-slate-100 my-3" />
              <div className="flex gap-4">
                <div className="h-3 w-20 bg-slate-100 rounded" />
                <div className="h-3 w-16 bg-slate-100 rounded" />
              </div>
            </div>
          ))
        ) : paginatedRows.length > 0 ? (
          paginatedRows.map((row, idx) => {
            const statusCfg = statusConfig[row.status] || statusConfig.NOT_APPLIED;
            const sColor    = row.timing ? (sessionColor[row.timing] || 'bg-slate-100 text-slate-600') : 'bg-slate-100 text-slate-400';
            return (
              <motion.div
                key={`${row.date}-${row.id}`}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: idx * 0.04 }}
              >
                <div className="p-4">
                  {/* Row 1 */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-[10px] font-bold shrink-0 ${sColor}`}>
                        {row.timing === 'FULL_DAY' ? 'FD' : row.timing === 'FIRST_HALF' ? 'FH' : row.timing === 'SECOND_HALF' ? 'SH' : '—'}
                      </div>
                      <div>
                        <p className="text-[13px] font-semibold text-slate-800 leading-tight">Holiday Duty</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">#{(currentPage - 1) * rowsPerPage + idx + 1}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${statusCfg.bg} ${statusCfg.text} ${statusCfg.border}`}>
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusCfg.dot}`} />
                        {statusCfg.label}
                      </span>
                      <div className="flex gap-1">

                        {row.status === 'NOT_APPLIED' && (
                          <motion.button
                            onClick={() => openApplyForm(row)}
                            className="px-3 py-1 text-[11px] font-semibold bg-slate-800 text-white rounded-full"
                            whileTap={{ scale: 0.92 }}
                          >Apply</motion.button>
                        )}

                        {canEdit(row) && (
                          <motion.button onClick={() => openEditForm(row)}
                            className="w-7 h-7 flex items-center justify-center rounded-xl bg-amber-50 text-amber-600"
                            whileTap={{ scale: 0.9 }}
                          ><FaRegEdit className="text-xs" /></motion.button>
                        )}

                        {(row.status === 'CANCELLED' || isExpired(row)) && (
                          <motion.button
                            onClick={() => { setViewRow(row); setShowViewModal(true); }}
                            className="w-7 h-7 flex items-center justify-center rounded-xl bg-blue-50 text-blue-500"
                            whileTap={{ scale: 0.9 }}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </motion.button>
                        )}

                        {row.type === 'COMP_PAY' && row.status === 'APPROVED' && (
                          <motion.button
                            onClick={() => { setViewRow(row); setShowViewModal(true); }}
                            className="w-7 h-7 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"
                            whileTap={{ scale: 0.9 }}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </motion.button>
                        )}

                      </div>
                    </div>
                  </div>

                  <div className="h-px bg-slate-100 my-3" />

                  {/* Row 2 */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
                      <BsCalendar4 className="text-slate-400 shrink-0" />
                      <span className="font-medium text-slate-700">{safeFormat(row.date, 'dd-MM-yyyy')}</span>
                    </div>
                    <TypeBadge type={row.type} />
                  </div>

                  {row.type === 'COMP_OFF' && row.status === 'APPROVED' && row.expiresOn && (
                    <p className="mt-2 text-[11px] text-slate-400">
                      <span className="font-medium text-slate-500">Expires: </span>
                      {safeFormat(row.expiresOn, 'dd-MM-yyyy')}
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center">
            <TbClockPlus className="text-4xl text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-400">No holiday duty records found</p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between px-1">
            <span className="text-[12px] text-slate-400 font-medium">
              Page <span className="text-slate-600 font-semibold">{currentPage}</span> of {totalPages}
            </span>
            <div className="flex items-center gap-1.5">
              <motion.button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`w-8 h-8 flex items-center justify-center rounded-xl border text-sm font-medium ${currentPage === 1 ? 'border-slate-100 text-slate-300 cursor-not-allowed' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                whileTap={{ scale: 0.92 }}
              >‹</motion.button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => totalPages <= 5 || p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
                .map((item, i) => item === '...' ? (
                  <span key={`e-${i}`} className="w-8 h-8 flex items-center justify-center text-slate-300 text-xs">…</span>
                ) : (
                  <motion.button
                    key={item} onClick={() => setCurrentPage(item)}
                    className={`w-8 h-8 flex items-center justify-center rounded-xl border text-[12px] font-semibold ${currentPage === item ? 'bg-slate-800 border-slate-800 text-white shadow-sm' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                    whileTap={{ scale: 0.92 }}
                  >{item}</motion.button>
                ))}
              <motion.button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={`w-8 h-8 flex items-center justify-center rounded-xl border text-sm font-medium ${currentPage === totalPages ? 'border-slate-100 text-slate-300 cursor-not-allowed' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                whileTap={{ scale: 0.92 }}
              >›</motion.button>
            </div>
          </div>
        )}
      </div>

      {/* ── Unified Apply / Edit form modal ───────────────────────────────────── */}
      {/* NOT inside AnimatePresence — prevents unmount/remount on session button click */}
      {showForm && formRow && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={closeForm}
        >
          <motion.div
            className="bg-white p-6 rounded-2xl shadow-xl max-w-sm w-full mx-4"
            initial={{ opacity: 0, scale: 0.92, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            onClick={e => e.stopPropagation()}
          >
            {/* Title */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-bold text-slate-800">{formTitle}</h3>
              <button
                onClick={closeForm}
                disabled={isSubmitting}
                className={`text-slate-400 hover:text-slate-600 text-xl leading-none ${isSubmitting ? 'opacity-40 cursor-not-allowed' : ''}`}
              >✕</button>
            </div>

            {/* Date chip */}
            <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-5">
              <BsCalendar4 className="text-slate-400 shrink-0" />
              <div>
                <p className="text-[11px] text-slate-400 leading-none mb-0.5">Date</p>
                <p className="text-sm font-semibold text-slate-700">{safeFormat(formRow.date, 'dd MMM yyyy')}</p>
              </div>
            </div>

            {/* Compensation type selector */}
            <div className="mb-5">
              <p className="text-sm font-medium text-slate-700 mb-3">Compensation Type</p>
              <TypeSelector value={formType} onChange={setFormType} />

              {/* Hint text when type is being switched in edit mode */}
              {formMode === 'edit' && formRow.type !== formType && (
                <p className="mt-2 text-[11px] text-slate-400 text-center">
                  {formType === 'COMP_OFF'
                    ? '⚡ Switching will auto-approve your Comp Off'
                    : '📋 Switching requires manager approval for Comp Pay'}
                </p>
              )}
            </div>

            {/* Session selector */}
            <div className="mb-6">
              <p className="text-sm font-medium text-slate-700 mb-3">Session</p>
              <SessionSelector value={formTiming} onChange={setFormTiming} />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeForm}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors disabled:opacity-40"
              >Cancel</button>
              <button
                type="button"
                onClick={handleFormSubmit}
                disabled={isSubmitting}
                className="px-5 py-2 text-sm font-medium bg-slate-800 text-white rounded-full hover:bg-slate-700 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-2 min-w-[100px] justify-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{formMode === 'edit' ? 'Updating…' : 'Submitting…'}</span>
                  </>
                ) : (
                  <span>{formMode === 'edit' ? 'Update' : 'Submit'}</span>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── View modal ────────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showViewModal && viewRow && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowViewModal(false)}
          >
            <motion.div
              className="bg-white p-6 rounded-2xl shadow-xl max-w-sm w-full mx-4"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base font-bold text-slate-800">
                  {viewRow.type ? TYPE_LABELS[viewRow.type] : 'Holiday Duty'} Details
                </h3>
                <button onClick={() => setShowViewModal(false)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">✕</button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Date</p>
                    <p className="text-sm font-semibold text-slate-800">{safeFormat(viewRow.date, 'dd-MM-yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Status</p>
                    <StatusPill status={viewRow.status} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Type</p>
                    <TypeBadge type={viewRow.type} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Session</p>
                    <p className="text-sm font-semibold text-slate-800">{viewRow.timing ? sessionLabel(viewRow.timing) : '—'}</p>
                  </div>
                </div>

                {viewRow.type === 'COMP_OFF' && viewRow.status === 'APPROVED' && viewRow.expiresOn && (
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Expires On</p>
                    <p className="text-sm font-semibold text-slate-800">{safeFormat(viewRow.expiresOn, 'dd-MM-yyyy')}</p>
                  </div>
                )}

                {viewRow.status === 'REJECTED' && viewRow.approverComment && (
                  <div className="p-3 rounded-xl bg-red-50 border border-red-100">
                    <p className="text-xs text-red-500 mb-1 font-medium">Rejection Reason</p>
                    <p className="text-sm text-red-700">{viewRow.approverComment}</p>
                  </div>
                )}

                {viewRow.type === 'COMP_PAY' && viewRow.status === 'APPROVED' && (
                  <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                    <p className="text-xs text-emerald-600 font-medium">
                      ✓ Comp Pay approved — your extra pay will be processed in the next payroll cycle.
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="w-full px-4 py-2.5 text-sm font-medium text-white bg-slate-700 hover:bg-slate-600 rounded-full transition-colors"
                >Close</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default E_Comps;